// Data Integrity Utilities for IndexedDB Security
// Provides hashing, signature verification, and tampering detection

import CryptoJS from 'crypto-js';

// Secret key for HMAC signatures (should be derived from user session)
const getSecretKey = () => {
  // In production, this should be derived from user session or server-provided key
  const userToken = localStorage.getItem('authToken') || '';
  return CryptoJS.SHA256(userToken).toString();
};

/**
 * Generate SHA-256 hash of critical data fields
 * @param {Object} data - The data object to hash
 * @returns {string} - SHA-256 hash string
 */
export const hashData = (data) => {
  if (!data || typeof data !== 'object') {
    return '';
  }

  // Extract critical fields for hashing (exclude metadata fields)
  const criticalFields = {
    ...data
  };
  
  // Remove metadata fields that shouldn't be part of hash
  delete criticalFields.dataHash;
  delete criticalFields.syncStatus;
  delete criticalFields.localModifiedAt;
  delete criticalFields.serverModifiedAt;
  delete criticalFields.conflictData;
  delete criticalFields.createdDate;
  delete criticalFields.updatedDate;
  delete criticalFields.signature;

  // Sort keys for consistent hashing
  const sortedData = Object.keys(criticalFields)
    .sort()
    .reduce((result, key) => {
      result[key] = criticalFields[key];
      return result;
    }, {});

  // Convert to JSON string and hash
  const jsonString = JSON.stringify(sortedData);
  return CryptoJS.SHA256(jsonString).toString();
};

/**
 * Verify data hash integrity
 * @param {Object} data - The data object to verify
 * @param {string} hash - The stored hash to compare against
 * @returns {boolean} - True if hash matches, false if tampered
 */
export const verifyDataHash = (data, hash) => {
  if (!hash) {
    return false; // No hash stored, consider tampered
  }

  const calculatedHash = hashData(data);
  return calculatedHash === hash;
};

/**
 * Generate HMAC signature for data
 * @param {Object} data - The data to sign
 * @param {string} secret - Optional secret key (uses default if not provided)
 * @returns {string} - HMAC signature
 */
export const signData = (data, secret = null) => {
  const secretKey = secret || getSecretKey();
  const jsonString = JSON.stringify(data);
  return CryptoJS.HmacSHA256(jsonString, secretKey).toString();
};

/**
 * Verify HMAC signature
 * @param {Object} data - The data to verify
 * @param {string} signature - The signature to verify against
 * @param {string} secret - Optional secret key (uses default if not provided)
 * @returns {boolean} - True if signature is valid
 */
export const verifySignature = (data, signature, secret = null) => {
  if (!signature) {
    return false; // No signature, consider invalid
  }

  const secretKey = secret || getSecretKey();
  const calculatedSignature = signData(data, secretKey);
  return calculatedSignature === signature;
};

/**
 * Check if data has been tampered with
 * @param {Object} record - The record to check
 * @returns {Object} - {isTampered: boolean, reason?: string}
 */
export const checkDataIntegrity = (record) => {
  if (!record || !record.dataHash) {
    return { isTampered: true, reason: 'No hash found' };
  }

  const isValid = verifyDataHash(record, record.dataHash);
  if (!isValid) {
    return { isTampered: true, reason: 'Hash mismatch - data may have been tampered' };
  }

  return { isTampered: false };
};

/**
 * Sign sync queue item with timestamp
 * @param {Object} queueItem - The sync queue item
 * @returns {string} - HMAC signature
 */
export const signSyncQueueItem = (queueItem) => {
  const dataToSign = {
    action: queueItem.action,
    entity: queueItem.entity,
    entityId: queueItem.entityId,
    payload: queueItem.payload,
    createdAt: queueItem.createdAt,
    userId: queueItem.userId || 'unknown'
  };
  
  return signData(dataToSign);
};

/**
 * Verify sync queue item signature
 * @param {Object} queueItem - The sync queue item
 * @returns {boolean} - True if signature is valid
 */
export const verifySyncQueueItem = (queueItem) => {
  if (!queueItem.signature) {
    return false;
  }

  const dataToVerify = {
    action: queueItem.action,
    entity: queueItem.entity,
    entityId: queueItem.entityId,
    payload: queueItem.payload,
    createdAt: queueItem.createdAt,
    userId: queueItem.userId || 'unknown'
  };

  return verifySignature(dataToVerify, queueItem.signature);
};

/**
 * Check if sync queue item is expired (older than 24 hours)
 * @param {Object} queueItem - The sync queue item
 * @returns {boolean} - True if expired
 */
export const isSyncQueueItemExpired = (queueItem) => {
  const now = Date.now();
  const itemAge = now - queueItem.createdAt;
  const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  
  return itemAge > maxAge;
};

/**
 * Generate secure random ID for database records
 * @returns {string} - Random ID
 */
export const generateSecureId = () => {
  const randomBytes = CryptoJS.lib.WordArray.random(16);
  return randomBytes.toString(CryptoJS.enc.Hex);
};

/**
 * Encrypt sensitive data for storage
 * @param {string} data - Data to encrypt
 * @param {string} key - Encryption key
 * @returns {string} - Encrypted data
 */
export const encryptData = (data, key = null) => {
  const encryptionKey = key || getSecretKey();
  return CryptoJS.AES.encrypt(data, encryptionKey).toString();
};

/**
 * Decrypt sensitive data
 * @param {string} encryptedData - Encrypted data
 * @param {string} key - Decryption key
 * @returns {string} - Decrypted data
 */
export const decryptData = (encryptedData, key = null) => {
  try {
    const decryptionKey = key || getSecretKey();
    const bytes = CryptoJS.AES.decrypt(encryptedData, decryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Failed to decrypt data:', error);
    return '';
  }
};

/**
 * Validate data structure before storage
 * @param {Object} data - Data to validate
 * @param {string} entityType - Type of entity (ORDER, EXPENSE, etc.)
 * @returns {Object} - {isValid: boolean, errors: string[]}
 */
export const validateDataStructure = (data, entityType) => {
  const errors = [];
  
  if (!data || typeof data !== 'object') {
    errors.push('Data must be an object');
    return { isValid: false, errors };
  }

  // Required fields based on entity type
  const requiredFields = {
    ORDER: ['orgId', 'accountId', 'name'],
    EXPENSE: ['orgId', 'accountId', 'category', 'amount'],
    CUSTOMER: ['orgId', 'name'],
    PRODUCT: ['orgId', 'name'],
    MONEY_ACCOUNT: ['orgId', 'name'],
    CATEGORY: ['orgId', 'name', 'type'],
    ORG_ACCOUNT: ['orgId', 'name']
  };

  const fields = requiredFields[entityType] || [];
  for (const field of fields) {
    if (!data[field] && data[field] !== 0) {
      errors.push(`Missing required field: ${field}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};



